/**
 * The routes, behind the sign-in gate. Until the session is known, only sign-in, an invitation link, About and Privacy
 * are shown; every other address shows sign-in in place, so the address asked for opens after signing in.
 *
 * PROVENANCE: C:\Olivia\web\src\App.tsx @ f92475e, copied 2026-10-12 (the gate of C:\Gap-Assessor\src\App.tsx @ 2023d32:
 * a 401 returns to sign-in; the session is looked at again on window focus; pages load on first render).
 * CHANGES: Pascal's routes; the demonstration flag from /api/auth/config; no reset notice yet.
 */

import { lazy, Suspense, useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Shell } from './components/Shell'
import { ErrorText, Loading } from './components/ui'
import { api } from './lib/api'
import { SessionProvider, useSessionState } from './lib/session'

const SignInPage = lazy(() => import('./pages/auth/SignInPage').then((m) => ({ default: m.SignInPage })))
const InvitePage = lazy(() => import('./pages/auth/InvitePage').then((m) => ({ default: m.InvitePage })))
const AboutPage = lazy(() => import('./pages/StaticPages').then((m) => ({ default: m.AboutPage })))
const PrivacyPage = lazy(() => import('./pages/StaticPages').then((m) => ({ default: m.PrivacyPage })))
const AtWorkPage = lazy(() => import('./pages/StaticPages').then((m) => ({ default: m.AtWorkPage })))
const LearningPage = lazy(() => import('./pages/LearningPage').then((m) => ({ default: m.LearningPage })))
const LayersPage = lazy(() => import('./pages/LayersPage').then((m) => ({ default: m.LayersPage })))
const HeadingPage = lazy(() => import('./pages/HeadingPage').then((m) => ({ default: m.HeadingPage })))
const DashboardsPage = lazy(() => import('./pages/DashboardsPage').then((m) => ({ default: m.DashboardsPage })))
const ProjectDashboardPage = lazy(() => import('./pages/DashboardsPage').then((m) => ({ default: m.ProjectDashboardPage })))
const YouDashboardPage = lazy(() => import('./pages/DashboardsPage').then((m) => ({ default: m.YouDashboardPage })))
const PlansPage = lazy(() => import('./pages/PlansPages').then((m) => ({ default: m.PlansPage })))
const TimelinePage = lazy(() => import('./pages/PlansPages').then((m) => ({ default: m.TimelinePage })))
const RoadmapPage = lazy(() => import('./pages/PlansPages').then((m) => ({ default: m.RoadmapPage })))
const WorkloadPage = lazy(() => import('./pages/PlansPages').then((m) => ({ default: m.WorkloadPage })))
const CalendarPage = lazy(() => import('./pages/PlansPages').then((m) => ({ default: m.CalendarPage })))
const FamilyPage = lazy(() => import('./pages/FamilyPage').then((m) => ({ default: m.FamilyPage })))
const AskPage = lazy(() => import('./pages/AskPage').then((m) => ({ default: m.AskPage })))
const HowPage = lazy(() => import('./pages/HowPage').then((m) => ({ default: m.HowPage })))
const AiPage = lazy(() => import('./pages/admin/AiPage').then((m) => ({ default: m.AiPage })))
const PrivacyPackPage = lazy(() => import('./pages/P7Pages').then((m) => ({ default: m.PrivacyPackPage })))
const WheelPage = lazy(() => import('./pages/P7Pages').then((m) => ({ default: m.WheelPage })))
const ImportPage = lazy(() => import('./pages/P7Pages').then((m) => ({ default: m.ImportPage })))
const IntakePage = lazy(() => import('./pages/P7Pages').then((m) => ({ default: m.IntakePage })))
const NotFoundPage = lazy(() => import('./pages/StaticPages').then((m) => ({ default: m.NotFoundPage })))
const BoardPage = lazy(() => import('./pages/WorkPages').then((m) => ({ default: m.BoardPage })))
const MyWorkPage = lazy(() => import('./pages/WorkPages').then((m) => ({ default: m.MyWorkPage })))
const OneBoardPage = lazy(() => import('./pages/work/BoardPage').then((m) => ({ default: m.BoardPage })))
const UnitsPage = lazy(() => import('./pages/admin/UnitsPage').then((m) => ({ default: m.UnitsPage })))
const PeoplePage = lazy(() => import('./pages/admin/PeoplePage').then((m) => ({ default: m.PeoplePage })))
const FourEyesPage = lazy(() => import('./pages/admin/FourEyesPage').then((m) => ({ default: m.FourEyesPage })))

/** The route patterns of the built screens. Tested in nav.test.ts against the navigation. */
export const BUILT_ROUTES = ['/board', '/boards/:id', '/my-work', '/learning', '/layers', '/heading', '/dashboards', '/dashboards/unit/:id', '/dashboards/project/:id', '/dashboards/you', '/plans', '/plans/timeline', '/plans/timeline/:id', '/plans/roadmap', '/plans/workload', '/plans/calendar', '/plans/wheel', '/family', '/import', '/intake', '/privacy/pack', '/ask', '/how', '/org/units', '/org/people', '/org/four-eyes', '/org/ai', '/about', '/privacy', '/privacy/at-work', '/invite'] as const

/** Whether the installation is a demonstration (PASCAL_DEMO_MODE): asked once, before sign-in too. */
function useDemo(): boolean {
  const [demo, setDemo] = useState(false)
  useEffect(() => {
    api.get<{ demo: boolean }>('auth/config').then(
      (c) => setDemo(c.demo === true),
      () => setDemo(false),
    )
  }, [])
  return demo
}

export default function App() {
  const { value, checked, unreachable, refresh } = useSessionState()
  const demo = useDemo()
  const signedIn = () => void refresh()

  if (!checked) return <Loading />

  if (!value) {
    return (
      <Suspense fallback={<Loading />}>
        {unreachable && (
          <div className="mx-auto max-w-md px-4 pt-6">
            <ErrorText error={new Error('unreachable')} onRetry={signedIn} />
          </div>
        )}
        <Routes>
          <Route path="/invite" element={<InvitePage onSignedIn={signedIn} />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/privacy/at-work" element={<AtWorkPage />} />
          <Route path="*" element={<SignInPage onSignedIn={signedIn} />} />
        </Routes>
      </Suspense>
    )
  }

  return (
    <SessionProvider value={value}>
      <Shell demo={demo}>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<Navigate to="/board" replace />} />
            <Route path="/board" element={<BoardPage />} />
            <Route path="/boards/:id" element={<OneBoardPage />} />
            <Route path="/my-work" element={<MyWorkPage />} />
            <Route path="/learning" element={<LearningPage />} />
            <Route path="/layers" element={<LayersPage />} />
            <Route path="/heading" element={<HeadingPage />} />
            <Route path="/dashboards" element={<DashboardsPage />} />
            <Route path="/dashboards/unit/:id" element={<DashboardsPage />} />
            <Route path="/dashboards/project/:id" element={<ProjectDashboardPage />} />
            <Route path="/dashboards/you" element={<YouDashboardPage />} />
            <Route path="/plans" element={<PlansPage />} />
            <Route path="/plans/timeline" element={<TimelinePage />} />
            <Route path="/plans/timeline/:id" element={<TimelinePage />} />
            <Route path="/plans/roadmap" element={<RoadmapPage />} />
            <Route path="/plans/workload" element={<WorkloadPage />} />
            <Route path="/plans/calendar" element={<CalendarPage />} />
            <Route path="/plans/wheel" element={<WheelPage />} />
            <Route path="/family" element={<FamilyPage />} />
            <Route path="/import" element={<ImportPage />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route path="/privacy/pack" element={<PrivacyPackPage />} />
            <Route path="/ask" element={<AskPage />} />
            <Route path="/how" element={<HowPage />} />
            <Route path="/org/units" element={<UnitsPage />} />
            <Route path="/org/people" element={<PeoplePage />} />
            <Route path="/org/four-eyes" element={<FourEyesPage />} />
            <Route path="/org/ai" element={<AiPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/privacy/at-work" element={<AtWorkPage />} />
            <Route path="/invite" element={<Navigate to="/board" replace />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Shell>
    </SessionProvider>
  )
}
